import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Icon, Modal, PageHeader, PageLayout, Segmented, Select, Skeleton, StatePanel, Tabs } from "../components/ui";
import { UserContext, EntitlementsContext } from "../contexts";
import { kpiGroups, NOT_AVAILABLE } from "./kpiInfo";
import { todayLine } from "./format";
import {
  useDashboardExecutive,
  useDashboardMe,
  useDashboardMyDay,
  useDashboardReports,
  useDashboardStore,
  useCacheOwner,
  useStoreKey,
} from "./useDashboardData";
import { useDashboardFreshness, useDashboardPersistence } from "./useDashboardFreshness";
import Freshness from "./components/Freshness";
import { useDashboardActionQueue } from "./useDashboardActionQueue";
import { useDashboardCta } from "./useDashboardCta";
import CtaModals from "./components/CtaModals";
import MyDayTab from "./tabs/MyDayTab";
import StoreTab from "./tabs/StoreTab";
import ExecutiveTab from "./tabs/ExecutiveTab";
import ReportsTab from "./tabs/ReportsTab";
import "./dashboard.scss";

// Dashboard v2 — /dashboard. Four tabs (?tab=day|store|executive|reports), the
// Store + Executive period (?period=week|month|quarter|year) and the Reports
// period (?report=week|month) live in the URL so a link opens the same view.
// Store-scoped: every query key carries the active store.

const TAB_IDS = ["day", "store", "executive", "reports"];
const PERIODS = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];
const REPORT_PERIODS = [
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];
const ALL = "all";

function KpiMap({ open, onClose }) {
  const groups = kpiGroups();
  return (
    <Modal open={open} onClose={onClose} title="KPI map" description="Every figure on the Dashboard and where it lives. Tap any i on the page for its formula." size="lg">
      <div className="dash-map">
        {Object.entries(groups).map(([tab, rows]) => (
          <div className="dash-map__group" key={tab}>
            <h4>{tab}</h4>
            {rows.map((r) => (
              <div className="dash-map__row" key={r.key}>
                <b>{r.name}</b>
                <span>{r.where}</span>
              </div>
            ))}
          </div>
        ))}
        <div className="dash-map__group dash-map__group--na">
          <h4>Not available yet</h4>
          {NOT_AVAILABLE.map(([name, why]) => (
            <div className="dash-map__row" key={name}>
              <b>{name}</b>
              <span>{why}</span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export function DashboardPage() {
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(UserContext) || {};
  const { hasFeature } = useContext(EntitlementsContext);
  const analyticsOn = hasFeature("analytics_core");
  const storeKey = useStoreKey();
  const userId = useCacheOwner();
  // The user's own stored copy is written from the query cache (fetched and
  // optimistic data alike); /version says when to revalidate.
  useDashboardPersistence(userId);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setParam = (changes) => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, v)));
    history.replace({ pathname: location.pathname, search: `?${next.toString()}` });
  };

  const me = useDashboardMe(storeKey, analyticsOn);
  const m = me.data || {};
  // Executive = the store owner (or RC master). Picking a rep = any manager
  // (owner / store_admin / master): /me says which via can_view_all_reps.
  const isOwner = !!m.can_view_executive;
  const isManager = !!(m.can_view_all_reps ?? m.can_view_executive);
  // Monthly targets, mirroring the backend (SalesTargetController, F1): a
  // manager of the active store or RC master sets the store target and any
  // rep's; everyone else only their own (GoalCard adds that case).
  const canSetTarget = isManager || !!user?.is_master_admin;

  const rawTab = params.get("tab") === "exec" ? "executive" : params.get("tab");
  const wantedTab = TAB_IDS.includes(rawTab) ? rawTab : "day";
  // Executive is the owner's; anyone else asking for it lands on My Day.
  const tab = wantedTab === "executive" && me.isSuccess && !isOwner ? "day" : wantedTab;
  const period = PERIODS.some((p) => p.value === params.get("period")) ? params.get("period") : "month";
  const reportPeriod = REPORT_PERIODS.some((p) => p.value === params.get("report")) ? params.get("report") : "week";
  const [reportOffset, setReportOffset] = useState(1);
  useEffect(() => setReportOffset(1), [reportPeriod]);

  // Rep shown on My Day: a manager picks (default_rep_id from /me — null for
  // managers = the whole store, which is sent by OMITTING rep_id); a rep always
  // sees themselves (rep_id omitted; the API defaults and enforces it).
  const [repChoice, setRepChoice] = useState(null);
  useEffect(() => setRepChoice(null), [storeKey]);
  const repSel = isManager ? repChoice ?? (m.default_rep_id ? String(m.default_rep_id) : ALL) : null;
  const repId = isManager && repSel && repSel !== ALL ? Number(repSel) : null;

  const ready = analyticsOn && me.isSuccess;
  const queue = useDashboardActionQueue({ repId, storeKey, enabled: ready && tab === "day" });
  const myDay = useDashboardMyDay(repId, storeKey, ready && tab === "day");
  const storeQ = useDashboardStore(period, storeKey, ready && tab === "store");
  const execQ = useDashboardExecutive(period, storeKey, ready && tab === "executive" && isOwner);
  const reportsQ = useDashboardReports(reportPeriod, reportOffset, storeKey, ready && tab === "reports");
  const cta = useDashboardCta({ markDone: queue.markDone, onChanged: queue.refresh });
  useDashboardFreshness({ storeKey, userId, enabled: ready });
  const visibleQueries =
    tab === "day" ? [queue.query, myDay] : tab === "store" ? Object.values(storeQ) : tab === "executive" ? Object.values(execQ) : [reportsQ];
  const [mapOpen, setMapOpen] = useState(false);

  const tabs = [
    { value: "day", label: "My Day" },
    { value: "store", label: "Store" },
    ...(isOwner ? [{ value: "executive", label: "Executive" }] : []),
    { value: "reports", label: "Reports" },
  ];

  const repOptions = [
    { value: ALL, label: "Whole store" },
    ...(m.reps || []).map((r) => ({ value: String(r.id), label: r.name })),
  ];

  const subtitle = [todayLine(), m.store?.name].filter(Boolean).join(" · ");

  let body;
  if (!analyticsOn) {
    body = (
      <StatePanel
        state="locked"
        title="Analytics isn’t in your plan"
        description="The Dashboard is built on it — your action queue, store KPIs and reports unlock with Analytics."
      />
    );
  } else if (me.isLoading) {
    body = <Skeleton shape="card" count={3} />;
  } else if (me.isError) {
    body = <StatePanel state="error" title="Couldn’t load the Dashboard" description="Try again in a moment." onRetry={() => me.refetch()} />;
  } else if (tab === "day") {
    body = <MyDayTab me={m} user={user} queue={queue} cta={cta} myDay={myDay} repId={repId} canSetTarget={canSetTarget} />;
  } else if (tab === "store") {
    body = (
      <StoreTab
        parts={storeQ}
        me={m}
        isManager={isManager}
        canSetTarget={canSetTarget}
        onViewRep={(id) => {
          setRepChoice(String(id));
          setParam({ tab: "day" });
        }}
      />
    );
  } else if (tab === "executive") {
    body = <ExecutiveTab parts={execQ} waAvailable={!!m.wa_available} storeKey={storeKey} canEdit={isOwner} />;
  } else {
    body = <ReportsTab q={reportsQ} period={reportPeriod} offset={reportOffset} setOffset={setReportOffset} />;
  }

  return (
    <PageLayout className="dash-page">
      <PageHeader
        title="Dashboard"
        description={subtitle}
        topbarActions={
          <Button variant="ghost" size="sm" data-btn="dashboard-kpi-map" leadingIcon={<Icon name="List" size="sm" />} hideLabelBelow="sm" onClick={() => setMapOpen(true)}>
            KPI map
          </Button>
        }
      />
      {analyticsOn ? (
        <div className="dash-toolbar">
          <Tabs aria-label="Dashboard sections" value={tab} onChange={(v) => setParam({ tab: v })} items={tabs} />
          <div className="dash-toolbar__right">
            {ready ? <Freshness key={`${tab}-${period}-${reportPeriod}`} queries={visibleQueries} /> : null}
            {tab === "day" && isManager && (m.reps || []).length ? (
              <Select label="Show rep" size="md" options={repOptions} value={repSel} onChange={(v) => setRepChoice(String(v ?? ALL))} className="dash-rep-select" />
            ) : null}
            {tab === "store" || tab === "executive" ? (
              <Segmented size="sm" label="Period" value={period} onChange={(v) => setParam({ period: v })} options={PERIODS} />
            ) : null}
            {tab === "reports" ? (
              <Segmented size="sm" label="Report period" value={reportPeriod} onChange={(v) => setParam({ report: v })} options={REPORT_PERIODS} />
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="dash-body">{body}</div>
      <CtaModals cta={cta} />
      <KpiMap open={mapOpen} onClose={() => setMapOpen(false)} />
    </PageLayout>
  );
}

export default DashboardPage;
